import { setEditMachineCountModal, showSuccessAlert } from "@actions/modals";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import {
  Icon,
  Label,
  Button,
  Header,
  Modal,
  Table,
  Input,
  TextArea,
  Form,
} from "semantic-ui-react";
import collections from "../../api/collections";
import { prettyMoney } from "@helpers/currency";
import { sum } from "lodash";
import CountModalSummary from "../SiteInstallations/CountModalSummary";

const denoms = [
  {
    label: "£50",
    key: "50",
  },
  {
    label: "£20",
    key: "20",
  },
  {
    label: "£10",
    key: "10",
  },
  {
    label: "£5",
    key: "5",
  },
  {
    label: "£1",
    key: "1",
  },
];

const EditMachineCountModal = (props) => {
  const {
    showCurr1,
    showCoins,
    open,
    activeRow,
    callback,
    newCoins,
    currCoins,
    loading,
    Comments,
  } = props.data;
  const { collectionId, collectionType } = activeRow;
  const { setEditMachineCountModal } = props;

  useEffect(() => {
    if (collectionId === null || collectionType === null || !open) {
      return;
    }

    collections
      .declareCountEdit(collectionType, collectionId)
      .then((response) => {
        const newValues = {};
        denoms.forEach(({ key }) => {
          newValues[`new${key}`] = response[`curr${key}`];
        });
        newValues.newCoins = response.currCoins;
        setEditMachineCountModal({ ...response, ...newValues });
      });
  }, [open, collectionId, collectionType, setEditMachineCountModal]);

  useEffect(() => {
    if (open) {
      return;
    }
    setEditMachineCountModal({ Comments: "" });
  }, [open]);

  const onClose = () =>
    props
      .setEditMachineCountModal({
        open: false,
        collectionId: null,
        collectionType: null,
      })
      .then(callback);

  const onProcess = () => {
    props.setEditMachineCountModal({ loading }).then(() => {
      const { new50, new20, new10, new1, new5 } = props.data;
      const params = {
        collectionId,
        collectionType,
        new50: Number(new50),
        new20: Number(new20),
        new10: Number(new10),
        new5: Number(new5),
        new1: Number(new1),
        newCoins: parseFloat(newCoins),
        Comments,
      };

      collections.declareCountSave(params).then(
        (response) =>
          props
            .setEditMachineCountModal({ loading: false })
            .then(() => props.showSuccessAlert(response, "Success", onClose)),
        () => props.setEditMachineCountModal({ loading: false }),
      );
    });
  };
  const sumByKey = (prefix) =>
    sum(denoms.map(({ key }) => Number(props.data[`${prefix}${key}`])));

  const sumChanged = () => {
    return sum(
      denoms.map(
        ({ key }) => props.data[`new${key}`] - props.data[`curr${key}`],
      ),
    );
  };

  const coinsDiff = newCoins - currCoins;

  const validFields = (fields) => {
    return fields.map(({ key }) => {
      const value = props.data[`new${key}`];

      if (key === "1" && !showCurr1) {
        return 1;
      }

      if (["", null].includes(value)) {
        return 0;
      }

      if (value < 0) {
        return 0;
      }

      if (key !== "Coins" && value % Number(key) !== 0) {
        return 0;
      }

      if (key === "Coins" && Math.round(value * 100) % 5 !== 0) {
        return 0;
      }
      return 1;
    });
  };

  const fields = [...denoms];

  if (showCoins) {
    fields.push({ key: "Coins", label: "Coins" });
  }

  const validFieldsCount = validFields(fields);
  const isValid =
    sum(validFieldsCount) === denoms.length + (showCoins ? 1 : 0) &&
    Comments.length > 0;

  return (
    <Modal size="small" open={open} onClose={onClose} closeIcon>
      <Header>Machine Count - Edit</Header>
      <Modal.Content>
        <CountModalSummary />
        <Table celled padded columns={4} attached="top">
          <Table.Row>
            <Table.HeaderCell>Denomination</Table.HeaderCell>
            <Table.HeaderCell>Current</Table.HeaderCell>
            <Table.HeaderCell collapsing>New</Table.HeaderCell>
            <Table.HeaderCell>Change</Table.HeaderCell>
          </Table.Row>
          {denoms.map(({ label, key }, index) => {
            const currentValue = props.data[`curr${key}`];
            const newValue = props.data[`new${key}`];
            const diff = newValue - currentValue;

            if (!showCurr1 && key === "1") {
              return;
            }

            return (
              <Table.Row
                key={`${key}_${index}`}
                positive={diff > 0}
                negative={diff < 0}
              >
                <Table.Cell className="p-1">{label}</Table.Cell>
                <Table.Cell>
                  <Input labelPosition="right">
                    £{prettyMoney(currentValue)}
                  </Input>
                </Table.Cell>
                <Table.Cell>
                  <Input
                    fluid
                    error={diff % Number(key) !== 0}
                    labelPosition="left"
                    type="text"
                    placeholder="Amount"
                    onChange={(e, { value }) =>
                      props.setEditMachineCountModal({ [`new${key}`]: value })
                    }
                  >
                    <Label error={diff % Number(key) !== 0}>£</Label>
                    <input
                      size="8"
                      type="number"
                      step={key}
                      value={props.data[`new${key}`]}
                      min="0"
                    />
                  </Input>
                </Table.Cell>
                <Table.Cell>
                  £{prettyMoney(diff)}
                  {diff !== 0 && (
                    <Icon
                      color={diff >= 0 ? "green" : "red"}
                      name={diff >= 0 ? "arrow up" : "arrow down"}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}

          {showCoins && (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell>Total Notes</Table.HeaderCell>
                <Table.HeaderCell>
                  £{prettyMoney(sumByKey("curr"))}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  £{prettyMoney(sumByKey("new"))}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  £{prettyMoney(sumChanged())}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          )}
        </Table>
        {showCoins && (
          <Table celled padded attached columns={4}>
            <Table.Row positive={coinsDiff > 0} negative={coinsDiff < 0}>
              <Table.Cell className="p-1">Total Coins</Table.Cell>
              <Table.Cell>
                <Input labelPosition="right">£{prettyMoney(currCoins)}</Input>
              </Table.Cell>
              <Table.Cell>
                <Input
                  fluid
                  labelPosition="left"
                  type="text"
                  placeholder="Amount"
                  onChange={(e, { value }) =>
                    props.setEditMachineCountModal({ newCoins: value })
                  }
                >
                  <Label>£</Label>
                  <input
                    size="8"
                    value={newCoins}
                    type="number"
                    step="0.05"
                    min="0"
                  />
                </Input>
              </Table.Cell>
              <Table.Cell>
                £{prettyMoney(newCoins - currCoins)}
                {coinsDiff !== 0 && (
                  <Icon
                    color={coinsDiff >= 0 ? "green" : "red"}
                    name={coinsDiff >= 0 ? "arrow up" : "arrow down"}
                  />
                )}
              </Table.Cell>
            </Table.Row>
          </Table>
        )}

        <Table celled padded attached="bottom" columns={4}>
          <Table.Header>
            <Table.Row>
              <Table.Cell className="p-1">Total Count</Table.Cell>
              <Table.Cell>
                £{prettyMoney(Number(sumByKey("curr") + currCoins))}
              </Table.Cell>
              <Table.Cell>
                £{prettyMoney(sumByKey("new") + Number(newCoins))}
              </Table.Cell>
              <Table.Cell>£{prettyMoney(sumChanged() + coinsDiff)}</Table.Cell>
            </Table.Row>
          </Table.Header>
        </Table>

        <Form>
          <Form.Field>
            <label>Comment:</label>
            <TextArea
              onChange={(e, { value }) =>
                props.setEditMachineCountModal({ Comments: value })
              }
              value={Comments}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative content="Cancel" onClick={onClose} />
        <Button
          onClick={onProcess}
          loading={loading}
          disabled={loading || !isValid}
          positive
          content="Process"
        />
      </Modal.Actions>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    data: state.editMachineCountModal,
  };
};

export default connect(mapStateToProps, {
  setEditMachineCountModal,
  showSuccessAlert,
})(EditMachineCountModal);
